import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, MapPin, Phone, Mail, Calendar, Users, ExternalLink, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function VenuePublic() {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/venues/${slug}`);
        if (!cancelled) {
          setVenue(data);
          setStatus("ok");
        }
      } catch {
        if (!cancelled) setStatus("notfound");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const mapSrc = useMemo(() => {
    if (!venue) return null;
    const a = venue.address || {};
    const q = encodeURIComponent(
      [a.line1, a.line2, a.city, a.postcode, a.country].filter(Boolean).join(", ")
    );
    return `https://maps.google.com/maps?q=${q}&hl=en&z=15&output=embed`;
  }, [venue]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-black text-[#F5F5F0] flex flex-col items-center justify-center px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">404</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">Venue not found</h1>
        <p className="text-[#A3A3A3]">This link doesn't exist, or the venue is no longer live.</p>
      </div>
    );
  }

  const v = venue;
  const openingByDay = new Array(7).fill(null).map((_, i) =>
    (v.opening_hours || []).find((h) => h.day === i) || { day: i, closed: true }
  );

  const fmtEventDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="venue-public-page" className="min-h-screen bg-black text-[#F5F5F0] grain">
      {/* HERO with cover */}
      <section className="relative overflow-hidden border-b border-white/10">
        {v.cover_data_uri ? (
          <div className="absolute inset-0">
            <img src={v.cover_data_uri} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-black" />
        )}
        <div className="relative z-[2] max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <div className="flex items-center gap-6 mb-8">
            {v.logo_data_uri && (
              <div className="h-24 w-24 md:h-28 md:w-28 bg-black/70 border border-white/10 flex items-center justify-center p-3 shrink-0">
                <img
                  src={v.logo_data_uri}
                  alt={`${v.name} logo`}
                  data-testid="venue-public-logo"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-3">Venue</div>
              <h1
                data-testid="venue-public-name"
                className="font-black tracking-[-0.03em] leading-[0.95]"
                style={{ fontSize: "clamp(2.25rem, 6vw, 5rem)" }}
              >
                {v.name}
              </h1>
            </div>
          </div>

          {/* quick contact */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            {v.address && (
              <span className="inline-flex items-center gap-2 text-[#F5F5F0]/90">
                <MapPin size={14} className="text-[#D9A6F5]" />
                {[v.address.line1, v.address.city, v.address.postcode].filter(Boolean).join(", ")}
              </span>
            )}
            {v.phone && (
              <a
                href={`tel:${v.phone}`}
                className="inline-flex items-center gap-2 text-[#F5F5F0]/90 hover:text-[#D9A6F5]"
              >
                <Phone size={14} className="text-[#D9A6F5]" /> {v.phone}
              </a>
            )}
            {v.email && (
              <a
                href={`mailto:${v.email}`}
                className="inline-flex items-center gap-2 text-[#F5F5F0]/90 hover:text-[#D9A6F5]"
              >
                <Mail size={14} className="text-[#D9A6F5]" /> {v.email}
              </a>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Left column — upcoming events */}
        <div className="lg:col-span-2">
          {v.memberships_enabled && (
            <div
              data-testid="venue-public-buy-membership-card"
              className="mb-10 border border-[#D9A6F5]/40 bg-[#D9A6F5]/5 p-6 md:p-8"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
                    Digital membership
                  </div>
                  <h3 className="text-xl md:text-2xl font-black tracking-tight text-[#F5F5F0]">
                    Not a member yet?
                  </h3>
                  <p className="mt-1 text-sm text-[#A3A3A3] max-w-md">
                    Buy a digital membership card, delivered instantly to your inbox — required
                    for members-only events.
                  </p>
                </div>
                <Link
                  to={`/v/${slug}/membership`}
                  data-testid="venue-public-buy-membership-btn"
                  className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-5 py-3 text-xs uppercase tracking-widest font-bold transition-colors"
                >
                  Buy Membership
                </Link>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-6">
            <Calendar size={14} /> Upcoming events
          </div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-10">
            What's on at {v.name}
          </h2>

          {v.upcoming_events?.length ? (
            <div className="space-y-4" data-testid="venue-public-events">
              {v.upcoming_events.map((ev, idx) => {
                const dateStr = ev.first_date || ev.occurrence_date;
                const href = ev.is_occurrence
                  ? `/v/${v.slug}/${ev.slug}?date=${dateStr}`
                  : `/v/${v.slug}/${ev.slug}`;
                return (
                  <Link
                    key={`${ev.id}-${dateStr}-${idx}`}
                    to={href}
                    data-testid={`venue-public-event-${ev.id}-${dateStr}`}
                    className="block bg-[#0A0A0A] border border-white/10 hover:border-[#D9A6F5]/40 p-6 md:p-7 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                      <div className="md:w-56 shrink-0">
                        <div className="text-xs uppercase tracking-[0.22em] text-[#D9A6F5]">
                          {fmtEventDate(dateStr)}
                        </div>
                        <div className="mt-1 text-2xl font-black tracking-tighter">
                          {ev.time || ""}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl md:text-2xl font-bold tracking-tight truncate">
                          {ev.name}
                        </h3>
                        {ev.theme && (
                          <div className="mt-1 text-xs uppercase tracking-[0.22em] text-[#D9A6F5]">
                            {ev.theme}
                          </div>
                        )}
                        {ev.audiences && ev.audiences.length > 0 && (
                          <div className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                            <Users size={11} className="text-[#D9A6F5]" />{" "}
                            {ev.audiences
                              .map((a) =>
                                ({
                                  everyone: "Everyone",
                                  couples: "Couples",
                                  tv: "TV",
                                  single_guys: "Single Guys",
                                  single_ladies: "Single Ladies",
                                }[a] || a)
                              )
                              .join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/10 p-10 text-center text-[#A3A3A3] uppercase tracking-[0.22em] text-sm">
              No upcoming events. Check back soon.
            </div>
          )}
        </div>

        {/* Right column — sidebar */}
        <aside className="space-y-8">
          {/* Opening hours */}
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-4">
              <Clock size={14} /> Opening times
            </div>
            <dl className="space-y-1.5 text-sm">
              {openingByDay.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-4">
                  <dt className="uppercase tracking-[0.16em] text-[#A3A3A3] font-medium text-xs">
                    {DAYS[i]}
                  </dt>
                  <dd className="text-[#F5F5F0]">
                    {h.closed || !h.open || !h.close ? (
                      <span className="text-[#A3A3A3]">Closed</span>
                    ) : (
                      <span className="font-mono">
                        {h.open}–{h.close}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Community profiles */}
          {(v.fabswingers_profile || v.swinghub_profile) && (
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-4">
                Community
              </div>
              <div className="space-y-3">
                {v.fabswingers_profile && (
                  <a
                    href={v.fabswingers_profile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 text-sm text-[#F5F5F0] hover:text-[#D9A6F5] transition-colors"
                  >
                    FabSwingers <ExternalLink size={12} />
                  </a>
                )}
                {v.swinghub_profile && (
                  <a
                    href={v.swinghub_profile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 text-sm text-[#F5F5F0] hover:text-[#D9A6F5] transition-colors"
                  >
                    SwingHub <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Map */}
          {mapSrc && (
            <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
              <div className="px-6 pt-6 pb-3 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
                <MapPin size={14} /> Find us
              </div>
              <iframe
                src={mapSrc}
                title={`Map of ${v.name}`}
                width="100%"
                height="260"
                style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="venue-public-map"
              />
              <div className="p-4 text-xs text-[#A3A3A3] leading-relaxed">
                {v.address?.line1}
                {v.address?.line2 ? `, ${v.address.line2}` : ""},<br />
                {v.address?.city} {v.address?.postcode}
                {v.address?.country ? `, ${v.address.country}` : ""}
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 text-center text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">
          Powered by{" "}
          <Link to="/" className="text-[#D9A6F5] hover:text-[#E8C8FF]">
            Swingle
          </Link>
        </div>
      </footer>
    </div>
  );
}
